import React, { useEffect, useState } from "react";
import { Button, Form, Table, Modal, Card, Row, Col, Alert } from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import PaymentTermsSelect from "../../components/common/PaymentTermsSelect";

export default function CreateInHouseSalesOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showItemModal, setShowItemModal] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Permission check
  const canCreateSO = useHasPermission(PERMISSIONS.SO_INHOUSE_CREATE);

  const handleOpenItemModal = () => setShowItemModal(true);
  const handleCloseItemModal = () => setShowItemModal(false);

  // Get today's date and tomorrow's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowString = tomorrow.toISOString().split('T')[0];

  const [orderData, setOrderData] = useState({
    orderNumber: "",
    buyerId: "",
    buyerBillTo: { name: "", address: "" },
    buyerDeliveryDetails: { name: "", address: "" },
    sameAsBillingAddress: true,
    buyerPONumber: "",
    buyerPOAttachment: "",
    broker: null,
    brokerId: null,
    deliveryTerms: "Ex Factory",
    paymentTerm: "", // Pre-filled for in-house orders
    transportation: false,
    transportationAmount: 0,
    taxAmount: 0,
    totalAmount: 0,
    remarks: "Internal transfer between company locations - Inter-company stock movement", // Pre-filled remark
    EDD: tomorrowString, // Default to tomorrow
    labTestRequired: false,
    orderDate: today,
  });

  const [newItem, setNewItem] = useState({
    productId: "",
    quality: "",
    quantity: 0,
    unit: "",
    unitPrice: 0,
    tax: 0,
    taxAmount: 0,
    baseAmount: 0,
    totalPrice: 0,
    grossOrNet: "Gross",
    labTestRequired: false,
    customMarking: "",
    packingSize: "",
    packingBagType: "",
  });

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [inHouseBuyers, setInHouseBuyers] = useState([]);
  const [buyersLoading, setBuyersLoading] = useState(true);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
 
  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/products?populateUnit=true&category=Finished Good`).then((res) => setProducts(res.data));
    // axios.get(`${config.API_BASE_URL}/brokers?approved=true&status=Active`).then((res) => setBrokers(res.data));
    // Fetch only in-house buyers
    axios.get(`${config.API_BASE_URL}/buyers?approved=true&status=Active&isInHouse=true`).then((res) => {
      setInHouseBuyers(res.data);
      setBuyersLoading(false);
      // Auto-select if only one in-house buyer
      if (res.data.length === 1) {
        setOrderData(prev => ({ ...prev, buyerId: res.data[0]._id }));
      }
    });
    axios.get(`${config.API_BASE_URL}/packing-materials`).then((res) => setMaterials(res.data));
  }, []);

  const handleOrderChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("buyerBillTo.") || name.startsWith("buyerDeliveryDetails.")) {
      const [parent, key] = name.split(".");
      setOrderData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [key]: value },
      }));
    } else {
      setOrderData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;

    setNewItem((prev) => {
      let updatedItem = { ...prev, [name]: value };
      if (name === "packingBagType") {
        const selectedMaterial = materials.find(m => m._id === value);
        setNewItem(prev => ({
          ...prev,
          packingBagType: value,
          packingSize: selectedMaterial?.size || ""  // auto-fill packing size
        }));
      }
      if (name === "productId") {
        const selectedProduct = products.find((p) => p._id === value);
        if (selectedProduct) {
          updatedItem = {
            ...updatedItem,
            tax: selectedProduct.gst || 0,
            unit: selectedProduct.unitId.unitCode || "",
          };
        }
        // Fetch filtered packing materials for this product
        fetchPackingMaterialsForProduct(value);
      }

      if (name === "quantity" || name === "unitPrice") {
        const baseAmount = updatedItem.quantity * updatedItem.unitPrice;
        const taxAmount = (baseAmount * (updatedItem.tax || 0)) / 100;
        updatedItem = {
          ...updatedItem,
          baseAmount,
          taxAmount,
          totalPrice: baseAmount + taxAmount,
        };
      }

      return updatedItem;
    });
  };

  // for showing unit in modal
  const selectedProduct = products.find((p) => p._id === newItem.productId);

  // Fetch packing materials for selected product
  const fetchPackingMaterialsForProduct = async (productId) => {
    if (!productId) {
      setFilteredMaterials([]);
      return;
    }
    try {
      const res = await axios.get(`${config.API_BASE_URL}/production-batches/packing-materials/product/${productId}?unfiltered=true`);
      setFilteredMaterials(res.data);
    } catch (err) {
      console.error('Error fetching packing materials:', err);
      setFilteredMaterials([]);
    }
  };

  useEffect(() => {
    const selectedBuyer = inHouseBuyers.find((b) => b._id === orderData.buyerId);

    if (selectedBuyer) {
      const formattedAddress = `${selectedBuyer.business_name}\n${selectedBuyer.address}\n${selectedBuyer.town}, ${selectedBuyer.city}, ${selectedBuyer.state} - ${selectedBuyer.pin_code}`;

      setOrderData((prev) => ({
        ...prev,
        buyerBillTo: {
          name: selectedBuyer.business_name,
          address: formattedAddress,
        },
        buyerDeliveryDetails: {
          name: selectedBuyer.business_name,
          address: formattedAddress,
        },
      }));
    }
  }, [orderData.buyerId, inHouseBuyers]);



  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
  if (!orderData.buyerId) {
    toast.error("In-house buyer is required");
    return;
  }

  const itemsWithCalc = items.map((it) => ({
    ...it,
    product: it.productId, // confirm if backend expects "product" or "productId"
  }));

  const subtotal = itemsWithCalc.reduce((sum, it) => sum + (it.baseAmount || 0), 0);
  const totalTax = itemsWithCalc.reduce((sum, it) => sum + (it.taxAmount || 0), 0);
  const totalAmount = subtotal + totalTax + (Number(orderData.transportationAmount) || 0);

  const payload = {
    ...orderData,
    buyer: orderData.buyerId || undefined,
    broker: orderData.brokerId || undefined,
    taxAmount: totalTax,
    totalAmount: totalAmount,
    items: itemsWithCalc,
    isInHouseOrder: true, // Mark as in-house order
  };

  setSubmitting(true);
  try {
    await axios.post(`${config.API_BASE_URL}/sales-orders`, payload);
    toast.success("In-House Sales Order created successfully");
    navigate("/sales-orders");
  } catch (err) {
    console.error("Error saving in-house sales order", err);
    toast.error("Error saving in-house sales order");
  } finally {
    setSubmitting(false);
  }
};

  const handleNextStep = () => {
    if (!orderData.buyerId) {
      toast.error("In-house buyer is required");
      return;
    }
    if (!orderData.buyerBillTo.address.trim()) {
      toast.error("Billing address is required");
      return;
    }
    if (!orderData.buyerDeliveryDetails.address.trim()) {
      toast.error("Delivery address is required");
      return;
    }
    if (!orderData.deliveryTerms) {
      toast.error("Delivery term is required");
      return;
    }
    setStep(2);
  };

  // Check permission
  if (!canCreateSO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/sales-orders">Sales Orders</Link>
            </li>
            <li className="breadcrumb-item active">Access Denied</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to create sales orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/sales-orders">Sales Orders</Link>
          </li>
          <li className="breadcrumb-item active">Create In-House</li>
        </ol>
      </nav>

      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0 me-3">Create In-House Sales Order</h2>
        <span className="badge bg-primary">🏢 Internal Transfer</span>
      </div>

      {inHouseBuyers.length === 0 && (
        <Alert variant="warning" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>No In-House Buyers Found</strong>
          <p className="mb-0">Please mark some buyers as "in-house" in the Buyers management section before creating in-house sales orders.</p>
        </Alert>
      )}

      {step === 1 && (
        <Form>
          <Row>
            {/* LEFT SIDE */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>In-House Buyer <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="buyerId"
                  value={orderData.buyerId}
                  onChange={handleOrderChange}
                  required
                  disabled={buyersLoading}
                >
                  <option value="">
                    {buyersLoading ? "Loading in-house buyers..." : inHouseBuyers.length === 0 ? "No in-house buyers available" : "Select in-house buyer..."}
                  </option>
                  {inHouseBuyers.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.business_name} 🏢
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Only buyers marked as "in-house" are shown here
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Broker</Form.Label>
                <Form.Select name="brokerId" value={orderData.brokerId} onChange={handleOrderChange} disabled>
                  <option value="">Not applicable for in-house orders</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Broker field is read-only for in-house orders
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Delivery Terms</Form.Label>
                <Form.Select name="deliveryTerms" value={orderData.deliveryTerms} onChange={handleOrderChange}>
                  <option value="">Select...</option>
                  <option value="Ex Factory">Ex Factory</option>
                  <option value="Bhada Paid">Bhada Paid</option>
                  <option value="FOB">FOB</option>
                  <option value="CIF">CIF</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Buyer PO Number</Form.Label>
                <Form.Control name="buyerPONumber" value={orderData.buyerPONumber} onChange={handleOrderChange} />
              </Form.Group>

              <Form.Group className="mb-3">
                <PaymentTermsSelect
                  value={orderData.paymentTerm}
                  onChange={(value) => setOrderData(prev => ({ ...prev, paymentTerm: value }))}
                />
              </Form.Group>
            </Col>

            {/* RIGHT SIDE */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Billing Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={orderData.buyerBillTo.address}
                  onChange={(e) =>
                    setOrderData((prev) => ({
                      ...prev,
                      buyerBillTo: { ...prev.buyerBillTo, address: e.target.value },
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Delivery Address same as Billing Address"
                  checked={orderData.sameAsBillingAddress}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setOrderData((prev) => ({
                      ...prev,
                      sameAsBillingAddress: isChecked,
                      buyerDeliveryDetails: isChecked
                        ? { ...prev.buyerBillTo }
                        : { name: "", address: "" },
                    }));
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Delivery Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={orderData.buyerDeliveryDetails.address}
                  disabled={orderData.sameAsBillingAddress}
                  onChange={(e) =>
                    setOrderData((prev) => ({
                      ...prev,
                      buyerDeliveryDetails: {
                        ...prev.buyerDeliveryDetails,
                        address: e.target.value,
                      },
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control as="textarea" name="remarks" value={orderData.remarks} onChange={handleOrderChange} />
            </Form.Group>
          </Row>

          <Row className="my-3">
            <Col className="text-end">
              <Button onClick={handleNextStep} disabled={inHouseBuyers.length === 0}>
                Next
              </Button>
            </Col>
          </Row>
        </Form>
      )}

      {step === 2 && (
        <>
          <Card>
            <Card.Body className="overflow-auto">
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quality</th>
                    <th>Weight</th>
                    <th>Packing Bag Type</th>
                    <th>Custom Marking</th>
                    <th>Unit Price (INR)</th>
                    <th>Tax (%)</th>
                    <th>Tax Amount (INR)</th>
                    <th>Total (INR)</th>
                    <th>Gross/Net</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const selectedMaterial = filteredMaterials.find((m) => m._id === it.packingBagType);
                    return (
                      <tr key={idx}>
                        <td>{products.find((p) => p._id === it.productId)?.productName}</td>
                        <td>{it.quality}</td>
                        <td>{it.quantity} {it.unit}</td>
                        <td>
                          {selectedMaterial ?
                            `${selectedMaterial.name} (${selectedMaterial.quantityPerBag} ${selectedMaterial.unit} per bag)` :
                            materials.find((m) => m._id === it.packingBagType)?.name || '-'
                          }
                        </td>
                        <td>{it.customMarking || '-'}</td>
                        <td>{it.unitPrice}</td>
                        <td>{it.tax}%</td>
                        <td>{it.taxAmount.toFixed(2)}</td>
                        <td>{it.totalPrice.toFixed(2)}</td>
                        <td>{it.grossOrNet}</td>
                        <td>
                          <Button variant="danger" size="sm" onClick={() => removeItem(idx)}>
                            Remove
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
          <div className="p-2">
            <Button variant="dark" onClick={handleOpenItemModal}>
              + Add New Item (Product)
            </Button>
          </div>
          <div className="row gy-3 mt-3">
            <Form.Group className="col-md-4">
              <Form.Check
                type="checkbox"
                label="Include Transportation ? (INR)"
                name="transportation"
                checked={orderData.transportation || false}
                onChange={(e) =>
                  setOrderData({
                    ...orderData,
                    transportation: e.target.checked,
                    transportationAmount: e.target.checked ? orderData.transportationAmount : 0,
                  })
                }
              />
              <Form.Control
                type="number"
                name="transportationAmount"
                value={orderData.transportationAmount || ""}
                onChange={handleOrderChange}
                disabled={!orderData.transportation}
              />
            </Form.Group>
          </div>

          <div className="row mt-4">
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label="Lab Test Required"
                name="labTestRequired"
                checked={orderData.labTestRequired}
                onChange={(e) =>
                  setOrderData((prev) => ({ ...prev, labTestRequired: e.target.checked }))
                }
              />
            </Form.Group>
          </div>

          <div className="row mt-2 gy-3">
            <Form.Group className="col-md-4">
              <Form.Label>Total Base Amount</Form.Label>
              <Form.Control
                readOnly
                value={(() => {
                  const subtotal = items.reduce((sum, it) => sum + (it.baseAmount || 0), 0);
                  return subtotal.toFixed(2);
                })()}
              />
            </Form.Group>

            <Form.Group className="col-md-4">
              <Form.Label>Total Tax (INR)</Form.Label>
              <Form.Control
                readOnly
                value={(() => {
                  const totalTax = items.reduce((sum, it) => sum + (it.taxAmount || 0), 0);
                  return totalTax.toFixed(2);
                })()}
              />
            </Form.Group>

            <Form.Group className="col-md-4">
              <Form.Label>Total Amount (With Tax)</Form.Label>
              <Form.Control
                readOnly
                value={(() => {
                  const subtotal = items.reduce((sum, it) => sum + (it.baseAmount || 0), 0);
                  const totalTax = items.reduce((sum, it) => sum + (it.taxAmount || 0), 0);
                  const transportation = orderData.transportation ? (Number(orderData.transportationAmount) || 0) : 0;
                  return (subtotal + totalTax + transportation).toFixed(2);
                })()}
              />
            </Form.Group>
          </div>
          <div className="row mt-3 gy-3">
            <Form.Group className="col-md-4 mb-3">
              <Form.Label>Expected Delivery Date (EDD)</Form.Label>
              <Form.Control
                type="date"
                name="EDD"
                value={orderData.EDD}
                onChange={handleOrderChange}
                // min={today}
              />
            </Form.Group>
            <Form.Group className="col-md-4 mb-3">
              <Form.Label>Order Date</Form.Label>
              <Form.Control
                type="date"
                name="orderDate"
                value={orderData.orderDate}
                onChange={handleOrderChange}
              />
            </Form.Group>
          </div>

          <div className="row justify-content-between my-4">
            <div className="col">
              <Button onClick={() => setStep(1)} disabled={submitting}>Back</Button>
            </div>
            <div className="col text-end">
              <Button variant="primary" onClick={handleSave} disabled={submitting}>
                {submitting ? "Saving..." : "Save In-House Order"}
              </Button>
            </div>
          </div>

          <Modal show={showItemModal} onHide={handleCloseItemModal} size="lg" fullscreen="lg-down">
            <Modal.Header closeButton>
              <Modal.Title>Add Product Item</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Row className="gy-3">
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Product</Form.Label>
                      <Form.Select name="productId" value={newItem.productId} onChange={handleItemChange}>
                        <option value="">Select...</option>
                        {products.map(p => (
                          <option key={p._id} value={p._id}>{p.productName}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Packing Bag Type</Form.Label>
                      <Form.Select name="packingBagType" value={newItem.packingBagType} onChange={handleItemChange}>
                        <option value="">Select...</option>
                        {filteredMaterials.map(m => (
                          <option key={m._id} value={m._id}>
                            {m.name} ({m.quantityPerBag} {m.unit} per bag)
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Packing Size</Form.Label>
                      <Form.Control name="packingSize" value={newItem.packingSize} onChange={handleItemChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Quality</Form.Label>
                      <Form.Control name="quality" value={newItem.quality} onChange={handleItemChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Quantity</Form.Label>
                      <div style={{ position: "relative" }}>
                        <Form.Control
                          type="number"
                          name="quantity"
                          value={newItem.quantity}
                          onChange={handleItemChange}
                        />
                        {selectedProduct && selectedProduct.unitId.unitCode && (
                          <span className="unit-suffix">
                            {selectedProduct.unitId.unitCode}
                          </span>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Unit Price (INR)</Form.Label>
                      <Form.Control
                        type="number"
                        name="unitPrice"
                        value={newItem.unitPrice}
                        onChange={handleItemChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Tax (%)</Form.Label>
                      <Form.Control
                        type="number"
                        name="tax"
                        value={newItem.tax}
                        onChange={handleItemChange}
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Tax Amount (INR)</Form.Label>
                      <Form.Control
                        type="number"
                        name="taxAmount"
                        value={newItem.taxAmount.toFixed(2)}
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Total Price (INR)</Form.Label>
                      <Form.Control
                        type="number"
                        name="totalPrice"
                        value={newItem.totalPrice.toFixed(2)}
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Gross/Net</Form.Label>
                      <Form.Select name="grossOrNet" value={newItem.grossOrNet} onChange={handleItemChange}>
                        <option value="Gross">Gross</option>
                        <option value="Net">Net</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Custom Marking</Form.Label>
                      <Form.Control
                        name="customMarking"
                        value={newItem.customMarking}
                        onChange={handleItemChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseItemModal}>Cancel</Button>
              <Button
                variant="success"
                onClick={() => {
                  if (!newItem.productId || !newItem.quantity || !newItem.unitPrice) {
                    toast.error("Please fill required fields");
                    return;
                  }
                  setItems(prev => [...prev, newItem]);
                  setNewItem({
                    productId: "",
                    quality: "",
                    quantity: 0,
                    unitPrice: 0,
                    grossOrNet: "Gross",
                    packingSize: "",
                    packingBagType: "",
                    EDD: tomorrowString,
                    labTestRequired: false,
                    customMarking: "",
                    taxAmount: 0,
                    totalPrice: 0,
                    tax: 0,
                  });
                  handleCloseItemModal();
                }}
              >
                Add Item
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Layout>
  );
}
